import { useEffect, useMemo, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

import { Button } from '~shared/shadcn/ui/button';
import { Input } from '~shared/shadcn/ui/input';
import { Label } from '~shared/shadcn/ui/label';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '~shared/shadcn/ui/sheet';
import { Switch } from '~shared/shadcn/ui/switch';
import { Textarea } from '~shared/shadcn/ui/textarea';
import { getErrorMessage } from '~shared/utils/error-handler';
import type {
  StudioFormConfigSummary,
  StudioFormFieldVisibility,
  StudioFormTheme,
} from '~features/form-submissions/api/types';
import { useUpdateFormConfig } from '../api/form-config-actions';
import {
  FORM_CONSENT_TEXT_MAX_LENGTH,
  FORM_INTEREST_OPTION_MAX_LENGTH,
  FORM_INTEREST_OPTIONS_MAX_COUNT,
  FORM_INTRO_TEXT_MAX_LENGTH,
  FORM_NAME_MAX_LENGTH,
  FORM_SUBMIT_LABEL_MAX_LENGTH,
  FORM_TITLE_MAX_LENGTH,
  getInterestOptionsLimitError,
  isOverFormFieldLimit,
  parseInterestOptions,
} from '../lib/form-validation';
import { EmbedSnippet } from './embed-snippet';
import { FieldLabelWithTooltip } from './field-label-with-tooltip';
import { StudioFormCard } from './studio-form-card';

function FieldCharCount({
  value,
  max,
}: {
  value: string;
  max: number;
}) {
  const over = isOverFormFieldLimit(value, max);
  return (
    <p
      className={
        over ? 'text-xs text-red-600' : 'text-xs text-muted-foreground'
      }
      role={over ? 'alert' : undefined}
    >
      {value.length}/{max}
      {over ? ' — shorten this field to save' : ''}
    </p>
  );
}

interface DraftState {
  formName: string;
  isActive: boolean;
  redirectUrl: string;
  googlePlaceId: string;
  portalSyncEnabled: boolean;
  ghlSyncEnabled: boolean;
  ghlTag: string;
  ghlSource: string;
  interestOptionsText: string;
  theme: StudioFormTheme;
  fieldVisibility: StudioFormFieldVisibility;
}

const toDraft = (config: StudioFormConfigSummary): DraftState => ({
  formName: config.formName,
  isActive: config.isActive,
  redirectUrl: config.redirectUrl ?? '',
  googlePlaceId: config.googlePlaceId ?? '',
  portalSyncEnabled: config.portalSyncEnabled,
  ghlSyncEnabled: config.ghlSyncEnabled,
  ghlTag: config.ghlTag ?? '',
  ghlSource: config.ghlSource ?? '',
  interestOptionsText: (config.interestOptions ?? []).join('\n'),
  theme: { ...config.theme },
  fieldVisibility: { ...config.fieldVisibility },
});

interface FormConfigEditorProps {
  config: StudioFormConfigSummary | null;
  studioId: string;
  studioName?: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function FormConfigEditor({
  config,
  studioId,
  studioName,
  open,
  onOpenChange,
}: FormConfigEditorProps) {
  const updateMutation = useUpdateFormConfig();
  const [draft, setDraft] = useState<DraftState | null>(null);

  useEffect(() => {
    if (config && open) setDraft(toDraft(config));
    if (!open) setDraft(null);
  }, [config, open]);

  const previewTheme = useMemo<StudioFormTheme>(
    () => draft?.theme ?? {},
    [draft],
  );

  if (!config || !draft) {
    return (
      <Sheet open={open} onOpenChange={onOpenChange}>
        <SheetContent className='sm:max-w-4xl' />
      </Sheet>
    );
  }

  const setTheme = (patch: Partial<StudioFormTheme>) =>
    { setDraft((prev) =>
      prev ? { ...prev, theme: { ...prev.theme, ...patch } } : prev,
    ); };
  const setFields = (patch: Partial<StudioFormFieldVisibility>) =>
    { setDraft((prev) =>
      prev
        ? { ...prev, fieldVisibility: { ...prev.fieldVisibility, ...patch } }
        : prev,
    ); };

  const interestOptionsError = getInterestOptionsLimitError(
    draft.interestOptionsText,
  );
  const copyLimitErrors = [
    isOverFormFieldLimit(draft.formName, FORM_NAME_MAX_LENGTH)
      ? 'Form name is too long'
      : null,
    isOverFormFieldLimit(draft.theme.formTitle ?? '', FORM_TITLE_MAX_LENGTH)
      ? 'Heading is too long'
      : null,
    isOverFormFieldLimit(draft.theme.introText ?? '', FORM_INTRO_TEXT_MAX_LENGTH)
      ? 'Intro text is too long'
      : null,
    isOverFormFieldLimit(
      draft.theme.submitLabel ?? '',
      FORM_SUBMIT_LABEL_MAX_LENGTH,
    )
      ? 'Submit label is too long'
      : null,
    isOverFormFieldLimit(
      draft.theme.consentText ?? '',
      FORM_CONSENT_TEXT_MAX_LENGTH,
    )
      ? 'Consent text is too long'
      : null,
    interestOptionsError,
  ].filter(Boolean) as string[];
  const hasCopyLimitError = copyLimitErrors.length > 0;

  const handleSave = () => {
    if (hasCopyLimitError) {
      toast.error(copyLimitErrors[0] ?? 'Shorten fields that are over the limit');
      return;
    }
    const interestOptions = parseInterestOptions(draft.interestOptionsText);

    updateMutation.mutate(
      {
        id: config._id,
        body: {
          formName: draft.formName.trim() || undefined,
          isActive: draft.isActive,
          redirectUrl: draft.redirectUrl.trim() || undefined,
          googlePlaceId: draft.googlePlaceId.trim() || undefined,
          portalSyncEnabled: draft.portalSyncEnabled,
          ghlSyncEnabled: draft.ghlSyncEnabled,
          ghlTag: draft.ghlTag.trim() || undefined,
          ghlSource: draft.ghlSource.trim() || undefined,
          interestOptions,
          theme: draft.theme,
          fieldVisibility: draft.fieldVisibility,
        },
      },
      {
        onSuccess: () => {
          toast.success('Form updated');
          onOpenChange(false);
        },
        onError: (mutationError) => {
          toast.error(getErrorMessage(mutationError, 'Could not save form'));
        },
      },
    );
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className='w-full overflow-y-auto sm:max-w-4xl'>
        <SheetHeader>
          <SheetTitle>Edit form</SheetTitle>
          <SheetDescription>
            {config.formName} - changes apply everywhere this form is embedded.
          </SheetDescription>
        </SheetHeader>

        <div className='mt-6 grid gap-8 lg:grid-cols-[1fr_360px]'>
          <div className='space-y-8 pb-10'>
            <section className='space-y-4'>
              <h3 className='text-sm font-semibold'>Basics</h3>
              <div className='space-y-1.5'>
                <Label htmlFor='fc-form-name'>Form name</Label>
                <Input
                  id='fc-form-name'
                  maxLength={FORM_NAME_MAX_LENGTH}
                  value={draft.formName}
                  onChange={(e) =>
                    { setDraft({ ...draft, formName: e.target.value }); }
                  }
                />
                <FieldCharCount value={draft.formName} max={FORM_NAME_MAX_LENGTH} />
              </div>
              <div className='flex items-center justify-between rounded-lg border p-3'>
                <div>
                  <p className='text-sm font-medium'>Active</p>
                  <p className='text-xs text-muted-foreground'>
                    Inactive forms reject new submissions.
                  </p>
                </div>
                <Switch
                  checked={draft.isActive}
                  onCheckedChange={(checked) =>
                    { setDraft({ ...draft, isActive: checked }); }
                  }
                />
              </div>
            </section>

            <section className='space-y-4'>
              <h3 className='text-sm font-semibold'>Copy</h3>
              <div className='space-y-1.5'>
                <Label htmlFor='fc-title'>Heading</Label>
                <Input
                  id='fc-title'
                  maxLength={FORM_TITLE_MAX_LENGTH}
                  value={draft.theme.formTitle ?? ''}
                  onChange={(e) => { setTheme({ formTitle: e.target.value }); }}
                />
                <FieldCharCount
                  value={draft.theme.formTitle ?? ''}
                  max={FORM_TITLE_MAX_LENGTH}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='fc-intro'>Intro text</Label>
                <Textarea
                  id='fc-intro'
                  rows={2}
                  maxLength={FORM_INTRO_TEXT_MAX_LENGTH}
                  value={draft.theme.introText ?? ''}
                  onChange={(e) => { setTheme({ introText: e.target.value }); }}
                />
                <FieldCharCount
                  value={draft.theme.introText ?? ''}
                  max={FORM_INTRO_TEXT_MAX_LENGTH}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='fc-submit-label'>Submit button label</Label>
                <Input
                  id='fc-submit-label'
                  maxLength={FORM_SUBMIT_LABEL_MAX_LENGTH}
                  value={draft.theme.submitLabel ?? ''}
                  onChange={(e) => { setTheme({ submitLabel: e.target.value }); }}
                />
                <FieldCharCount
                  value={draft.theme.submitLabel ?? ''}
                  max={FORM_SUBMIT_LABEL_MAX_LENGTH}
                />
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='fc-consent'>Consent text</Label>
                <Textarea
                  id='fc-consent'
                  rows={3}
                  maxLength={FORM_CONSENT_TEXT_MAX_LENGTH}
                  value={draft.theme.consentText ?? ''}
                  onChange={(e) => { setTheme({ consentText: e.target.value }); }}
                />
                <FieldCharCount
                  value={draft.theme.consentText ?? ''}
                  max={FORM_CONSENT_TEXT_MAX_LENGTH}
                />
              </div>
            </section>

            <section className='space-y-4'>
              <h3 className='text-sm font-semibold'>Brand</h3>
              <div className='flex items-center gap-3'>
                <div className='space-y-1.5'>
                  <FieldLabelWithTooltip
                    htmlFor='fc-color'
                    label='Primary color'
                    tip='Used for the submit button and accent highlights on the embedded form. Preview updates on the right.'
                  />
                  <div className='flex items-center gap-2'>
                    <input
                      id='fc-color'
                      type='color'
                      className='h-9 w-12 cursor-pointer rounded-md border'
                      value={draft.theme.primaryColor ?? '#6d28d9'}
                      onChange={(e) =>
                        { setTheme({ primaryColor: e.target.value }); }
                      }
                    />
                    <Input
                      className='w-28 font-mono'
                      value={draft.theme.primaryColor ?? ''}
                      placeholder='#6d28d9'
                      onChange={(e) =>
                        { setTheme({ primaryColor: e.target.value }); }
                      }
                    />
                  </div>
                </div>
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='fc-logo'>Logo URL</Label>
                <Input
                  id='fc-logo'
                  type='url'
                  placeholder='https://...'
                  value={draft.theme.logoUrl ?? ''}
                  onChange={(e) => { setTheme({ logoUrl: e.target.value }); }}
                />
              </div>
            </section>

            <section className='space-y-4'>
              <h3 className='text-sm font-semibold'>Fields</h3>
              <div className='space-y-3'>
                <div className='flex items-center justify-between'>
                  <Label htmlFor='fc-f-child' className='font-normal'>
                    Student&apos;s first name
                  </Label>
                  <Switch
                    id='fc-f-child'
                    checked={draft.fieldVisibility.childFirstName ?? true}
                    onCheckedChange={(checked) =>
                      { setFields({ childFirstName: checked }); }
                    }
                  />
                </div>
                <div className='flex items-center justify-between'>
                  <Label htmlFor='fc-f-age' className='font-normal'>
                    Student&apos;s age
                  </Label>
                  <Switch
                    id='fc-f-age'
                    checked={draft.fieldVisibility.childAge ?? false}
                    onCheckedChange={(checked) =>
                      { setFields({ childAge: checked }); }
                    }
                  />
                </div>
                <div className='flex items-center justify-between'>
                  <Label htmlFor='fc-f-interest' className='font-normal'>
                    Interest options
                  </Label>
                  <Switch
                    id='fc-f-interest'
                    checked={draft.fieldVisibility.interest ?? true}
                    onCheckedChange={(checked) =>
                      { setFields({ interest: checked }); }
                    }
                  />
                </div>
                <div className='flex items-center justify-between'>
                  <Label htmlFor='fc-f-interest-dd' className='font-normal'>
                    Show interest as dropdown
                  </Label>
                  <Switch
                    id='fc-f-interest-dd'
                    checked={draft.fieldVisibility.interestAsDropdown ?? false}
                    onCheckedChange={(checked) =>
                      { setFields({ interestAsDropdown: checked }); }
                    }
                  />
                </div>
              </div>
              <div className='space-y-1.5'>
                <Label htmlFor='fc-interest-options'>
                  Interest options (one per line)
                </Label>
                <Textarea
                  id='fc-interest-options'
                  rows={5}
                  value={draft.interestOptionsText}
                  onChange={(e) =>
                    { setDraft({
                      ...draft,
                      interestOptionsText: e.target.value,
                    }); }
                  }
                />
                <p
                  className={
                    interestOptionsError
                      ? 'text-xs text-red-600'
                      : 'text-xs text-muted-foreground'
                  }
                  role={interestOptionsError ? 'alert' : undefined}
                >
                  {interestOptionsError ??
                    `Up to ${FORM_INTEREST_OPTIONS_MAX_COUNT} options, ${FORM_INTEREST_OPTION_MAX_LENGTH} characters each.`}
                </p>
              </div>
            </section>

            <section className='space-y-4'>
              <h3 className='text-sm font-semibold'>Where submissions go</h3>
              <div className='flex items-center justify-between rounded-lg border p-3'>
                <div>
                  <p className='text-sm font-medium'>Save in Studio Portal</p>
                  <p className='text-xs text-muted-foreground'>
                    Creates or updates the family contact in Enrollio so your
                    staff can see the lead without opening the CRM.
                  </p>
                </div>
                <Switch
                  checked={draft.portalSyncEnabled}
                  onCheckedChange={(checked) =>
                    { setDraft({ ...draft, portalSyncEnabled: checked }); }
                  }
                />
              </div>
              <div className='flex items-center justify-between rounded-lg border p-3'>
                <div>
                  <p className='text-sm font-medium'>Sync to CRM</p>
                  <p className='text-xs text-muted-foreground'>
                    Creates or updates the contact in your CRM and applies the
                    tag below so follow-up automations can fire.
                  </p>
                </div>
                <Switch
                  checked={draft.ghlSyncEnabled}
                  onCheckedChange={(checked) =>
                    { setDraft({ ...draft, ghlSyncEnabled: checked }); }
                  }
                />
              </div>
              <div className='grid grid-cols-1 gap-4 sm:grid-cols-2'>
                <div className='space-y-1.5'>
                  <FieldLabelWithTooltip
                    htmlFor='fc-ghl-tag'
                    label='CRM tag'
                    tip='This tag is added to the contact in your CRM when someone submits this form. Use it to trigger follow-up workflows.'
                  />
                  <Input
                    id='fc-ghl-tag'
                    value={draft.ghlTag}
                    placeholder='e.g. website lead capture form'
                    onChange={(e) =>
                      { setDraft({ ...draft, ghlTag: e.target.value }); }
                    }
                  />
                </div>
                <div className='space-y-1.5'>
                  <FieldLabelWithTooltip
                    htmlFor='fc-ghl-source'
                    label='CRM source'
                    tip='Shown on the CRM contact as where this lead came from (for example Enrollio Forms).'
                  />
                  <Input
                    id='fc-ghl-source'
                    value={draft.ghlSource}
                    placeholder='e.g. Enrollio Website Lead Capture Form'
                    onChange={(e) =>
                      { setDraft({ ...draft, ghlSource: e.target.value }); }
                    }
                  />
                </div>
              </div>
              <div className='space-y-1.5'>
                <FieldLabelWithTooltip
                  htmlFor='fc-redirect'
                  label='Redirect URL after submit'
                  tip='Optional. After a successful submit, the parent is sent to this page (for example a thank-you page).'
                />
                <Input
                  id='fc-redirect'
                  type='url'
                  placeholder='https://...'
                  value={draft.redirectUrl}
                  onChange={(e) =>
                    { setDraft({ ...draft, redirectUrl: e.target.value }); }
                  }
                />
              </div>
            </section>

            <section className='space-y-3'>
              <h3 className='text-sm font-semibold'>Embed</h3>
              <EmbedSnippet
                studioId={studioId}
                formType={config.formType}
                formId={config._id}
                formName={draft.formName || config.formName}
              />
            </section>

            <div className='sticky bottom-0 flex justify-end gap-2 border-t bg-background py-4'>
              <Button
                variant='outline'
                onClick={() => { onOpenChange(false); }}
                disabled={updateMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSave}
                disabled={updateMutation.isPending || hasCopyLimitError}
              >
                {updateMutation.isPending ? (
                  <>
                    <Loader2 className='mr-2 size-4 animate-spin' />
                    Saving...
                  </>
                ) : (
                  'Save changes'
                )}
              </Button>
            </div>
          </div>

          <div className='hidden lg:block'>
            <div className='sticky top-0 space-y-2'>
              <p className='text-xs font-medium uppercase tracking-wide text-muted-foreground'>
                Preview
              </p>
              <div className='flex justify-center overflow-hidden rounded-xl bg-slate-100 p-4'>
                <StudioFormCard
                  theme={previewTheme}
                  fieldVisibility={draft.fieldVisibility}
                  interestOptions={parseInterestOptions(
                    draft.interestOptionsText,
                  )}
                  studioName={studioName}
                  interactive={false}
                />
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
}
